import { useState } from "react";
import { Link, Outlet, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { LogOut, Menu, Pencil, Settings } from "lucide-react";
import { Card, Badge, NavItem } from "@onemileauto/ui";
import { useMailbox } from "../../hooks/use-mailbox";
import { useMailboxSocket } from "../../hooks/use-mailbox-socket";
import { FolderNav } from "../../components/mail/folder-nav";
import { FolderDrawer } from "../../components/mail/folder-drawer";
import { MailboxSwitcher } from "../../components/mail/mailbox-switcher";
import { SearchBar } from "../../components/mail/search-bar";
import { ComposeSheet } from "../../components/mail/compose-sheet";
import { SendToastStack } from "../../components/mail/send-toast-stack";
import { logout } from "../../lib/auth-client";
import type { MailSearchParams } from "../../router";

/** Shared by the desktop rail and mobile drawer — signs out and hard-navigates to /login (see lib/auth-client.ts; a hard nav is the simplest correct way to discard any in-memory session/query state). */
async function handleLogout() {
  await logout();
  window.location.href = "/login";
}

/**
 * Gmail-style shell — explicitly NOT Outlook's persistent list+detail
 * split. Two structural regions:
 *
 *  - A folder rail: persistent at every viewport size where there's room
 *    (`md:` and up, a real left column). On narrow viewports it collapses
 *    behind a hamburger-triggered drawer (FolderDrawer) instead of
 *    disappearing — Gmail's own mobile pattern.
 *  - A single main pane that *navigates* between list-mode and
 *    detail-mode via the router (`/` vs `/$threadId` — see router.tsx),
 *    rather than showing both at once. Selecting a thread replaces the
 *    list with the thread in the SAME pane at every viewport size; there
 *    is no side-by-side split here anymore. Each route component
 *    (MailIndexPane / ThreadDetailRoute) owns its own content and reads
 *    the `folder`/`q`/`threadId` route state itself.
 *
 * The search bar is mounted once, here, at the top of the main pane — not
 * inside the list route — so it reads as a persistent/global affordance
 * that's visible in both list and detail mode, the way Gmail's is.
 *
 * `folder`/`q` live as URL search params (not path segments) so they
 * persist across navigating into and back out of a thread — see
 * router.tsx.
 */
export function MailShell() {
  const { mailboxId, selfEmail, isLoading, isError, data, mailboxes, currentMailbox, isAggregate, isOwnerOfAny } =
    useMailbox();
  const search = useSearch({ strict: false }) as MailSearchParams;
  const params = useParams({ strict: false }) as { threadId?: string };
  const rawNavigate = useNavigate();
  const [composeOpen, setComposeOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // See prior note in this file's history: TanStack Router's strict
  // search-param typing infers `from` the ROOT route by default for a
  // generic useNavigate() call, and the root has no validateSearch — only
  // the pathless "mail-layout" route (this component) does. That mismatch
  // makes the search updater's inferred type collapse to `never` even
  // though MailSearchParams is the real, validated shape in effect at
  // runtime. Narrowing the cast here (once) keeps every call site below
  // honestly typed.
  const navigate = rawNavigate as unknown as (opts: {
    to?: string;
    search: (prev: MailSearchParams) => MailSearchParams;
  }) => void;

  const updateSearch = (updater: (prev: MailSearchParams) => MailSearchParams) => {
    navigate({ search: updater });
  };

  useMailboxSocket(mailboxId);

  const hasSelectedThread = Boolean(params.threadId);
  const activeQuery = search.q ?? "";
  const activeFolder = search.folder ?? "inbox";

  function handleSearchChange(q: string) {
    // The search bar is visible from detail mode too (Gmail keeps it
    // reachable from anywhere) — typing a query there should jump back to
    // the single main pane's list-mode to show results, not sit inert
    // behind an open thread.
    if (q && hasSelectedThread) {
      navigate({ to: "/", search: (prev) => ({ ...prev, q }) });
    } else {
      updateSearch((prev) => ({ ...prev, q: q || undefined }));
    }
  }

  function handleSelectFolder(slug: string) {
    if (hasSelectedThread) {
      navigate({ to: "/", search: (prev) => ({ ...prev, folder: slug, q: undefined }) });
    } else {
      updateSearch((prev) => ({ ...prev, folder: slug, q: undefined }));
    }
  }

  // Switching mailboxes drops any in-progress search (per-mailbox, doesn't
  // carry over) and resets to Inbox — "All Inboxes" only ever shows Inbox
  // (see AggregateThreadList's docstring for why), and landing a real
  // mailbox switch on whatever folder happened to be selected before is
  // more likely to look like a bug ("where did my mail go?") than a
  // feature for a non-technical user.
  function handleSelectMailbox(mailboxIdOrAll: string) {
    const nextSearch = (prev: MailSearchParams): MailSearchParams => ({
      ...prev,
      mailbox: mailboxIdOrAll,
      folder: "inbox",
      q: undefined,
    });
    if (hasSelectedThread) {
      navigate({ to: "/", search: nextSearch });
    } else {
      updateSearch(nextSearch);
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-sm text-muted">Loading your mailbox&hellip;</p>
      </div>
    );
  }

  // isAggregate ("All Inboxes") legitimately has no single mailboxId — only
  // treat a missing mailboxId as an error outside that mode.
  if (isError || (!mailboxId && !isAggregate)) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Card className="w-full max-w-sm p-6 text-center">
          <p className="font-display text-lg text-charcoal">Couldn&rsquo;t reach the mail API</p>
          <p className="mt-2 text-sm text-muted">Check your connection and reload the page.</p>
        </Card>
      </div>
    );
  }

  if (data && data.mailboxes.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Card className="w-full max-w-sm p-6 text-center">
          <Badge variant="muted">No mailbox</Badge>
          <p className="mt-3 text-sm text-muted">This account isn&rsquo;t attached to a mailbox yet.</p>
        </Card>
      </div>
    );
  }

  // "All Inboxes" is Inbox-only and read-only (see AggregateThreadList) —
  // folder switching, compose, and search all need one specific mailbox to
  // mean anything, so they're hidden rather than shown-but-broken while
  // this mode is active.
  const canCompose = !isAggregate;
  const canSearch = !isAggregate;
  // Folder CRUD (Phase 6) is gated server-side at owner/full ("folder_admin"
  // in lib/acl.ts) — mirror that client-side so read_only/send_as users see
  // a plain folder list with no create/manage affordances that would just
  // 404 on submit, rather than a control that looks available but isn't.
  const canManageFolders =
    !isAggregate && (currentMailbox?.permissionLevel === "owner" || currentMailbox?.permissionLevel === "full");

  return (
    <div className="flex h-full min-h-0 flex-1">
      {/* Persistent folder rail — desktop/tablet only; collapses to the
          drawer below on narrow viewports. */}
      <aside className="hidden w-64 shrink-0 flex-col gap-4 border-r border-border p-3 md:flex">
        <MailboxSwitcher
          mailboxes={mailboxes}
          currentMailboxId={mailboxId}
          isAggregate={isAggregate}
          onSelect={handleSelectMailbox}
        />
        {!isAggregate && (
          <FolderNav
            mailboxId={mailboxId}
            activeSlug={activeFolder}
            onSelect={handleSelectFolder}
            canManageFolders={canManageFolders}
          />
        )}
        <div className="mt-auto border-t border-border pt-2">
          {isOwnerOfAny && (
            <Link to="/admin">
              <NavItem icon={Settings} label="Manage mailboxes" />
            </Link>
          )}
          <NavItem icon={LogOut} label="Log out" onClick={handleLogout} />
        </div>
      </aside>

      {/* Single main pane: top bar (hamburger + global search) is fixed
          chrome, the Outlet below it is what actually navigates between
          list-mode and detail-mode. `min-w-0` is load-bearing here, not
          decorative: without it this flex item won't shrink below its
          content's intrinsic width, so a long thread subject in
          DetailHeader's `truncate` title forces the whole pane (and
          everything in it) to overflow horizontally instead of actually
          truncating — found while verifying this pass's thread-detail
          changes with a realistic subject line. */}
      <section className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5 md:px-4">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open folders"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-charcoal hover:bg-parchment-dim md:hidden"
          >
            <Menu size={22} />
          </button>
          {canSearch ? (
            <SearchBar className="flex-1" value={activeQuery} onChange={handleSearchChange} />
          ) : (
            <p className="flex-1 text-sm text-muted">Open a mailbox to search its mail.</p>
          )}
        </div>

        <Outlet />

        {/* Compose FAB only makes sense in list-mode — in detail-mode the
            pinned Reply/Forward bar (see ThreadDetail) is the composing
            entry point, matching Gmail's own affordances. */}
        {!hasSelectedThread && canCompose && (
          <button
            type="button"
            onClick={() => setComposeOpen(true)}
            aria-label="New message"
            className="absolute bottom-5 right-5 flex h-14 w-14 items-center justify-center rounded-full bg-rust text-parchment shadow-lg transition-colors hover:bg-rust-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
          >
            <Pencil size={22} />
          </button>
        )}
      </section>

      {drawerOpen && (
        <FolderDrawer
          mailboxId={mailboxId}
          activeSlug={activeFolder}
          onSelect={handleSelectFolder}
          onClose={() => setDrawerOpen(false)}
          mailboxes={mailboxes}
          isAggregate={isAggregate}
          onSelectMailbox={handleSelectMailbox}
          isOwnerOfAny={isOwnerOfAny}
          canManageFolders={canManageFolders}
        />
      )}

      {composeOpen && canCompose && (
        // Non-null: canCompose === !isAggregate, and the guard above already
        // returned early for !mailboxId && !isAggregate — so mailboxId is
        // always defined here. TS can't follow that boolean algebra itself.
        <ComposeSheet
          mailboxId={mailboxId!}
          selfEmail={selfEmail}
          mode="new"
          signature={currentMailbox?.signature}
          onClose={() => setComposeOpen(false)}
        />
      )}
      <SendToastStack mailboxId={mailboxId} />
    </div>
  );
}
